/**
 * WialonService - Centralized Wialon API Service
 * 
 * Provides API calls with:
 * - jQuery-compatible JSONP handling
 * - Retry mechanism with exponential backoff
 * - Request timeout handling
 * - Error classification
 */

const DEFAULT_TIMEOUT = 15000; // 15 seconds
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

/**
 * Custom error class for Wialon API errors
 */
class WialonError extends Error {
    constructor(message, code, isAuthError = false) {
        super(message);
        this.name = 'WialonError';
        this.code = code;
        this.isAuthError = isAuthError;
    }
}

/**
 * Generate a unique callback name for JSONP
 */
let callbackCounter = 0;
const generateCallbackName = () => {
    callbackCounter++;
    return `wialonCallback${callbackCounter}${Date.now()}`;
};

/**
 * Make a JSONP request (required for Wialon cross-origin API)
 * This implementation is compatible with Wialon's JSONP format
 * @param {string} url - The URL to request
 * @param {number} timeout - Request timeout in ms
 * @returns {Promise<any>} - Response data
 */
const jsonpRequest = (url, timeout = DEFAULT_TIMEOUT) => {
    return new Promise((resolve, reject) => {
        const callbackName = generateCallbackName();
        const script = document.createElement('script');
        let timeoutId;
        let resolved = false;

        // Cleanup function
        const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
            // Delay callback cleanup to ensure response is processed
            setTimeout(() => {
                try {
                    delete window[callbackName];
                } catch (e) {
                    window[callbackName] = undefined;
                }
            }, 100);
        };

        // Setup callback on window
        window[callbackName] = (data) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            resolve(data);
        };

        // Setup timeout
        timeoutId = setTimeout(() => {
            if (resolved) return;
            resolved = true;
            cleanup();
            reject(new WialonError('Request timeout', 'TIMEOUT'));
        }, timeout);

        // Setup error handler
        script.onerror = (e) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            reject(new WialonError('Network error: Failed to load script', 'NETWORK_ERROR'));
        };

        // Add callback to URL - Wialon expects 'callback' parameter
        const separator = url.includes('?') ? '&' : '?';
        script.src = `${url}${separator}callback=${callbackName}`;
        script.async = true;
        script.charset = 'UTF-8';

        // Execute request
        document.head.appendChild(script);
    });
};

/**
 * Execute a request with retry logic
 * @param {Function} requestFn - Function that returns a promise
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise<any>} - Response data
 */
const withRetry = async (requestFn, maxRetries = MAX_RETRIES) => {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await requestFn();
        } catch (error) {
            lastError = error;

            // Don't retry auth errors
            if (error.isAuthError) {
                throw error;
            }

            // Don't retry on last attempt
            if (attempt === maxRetries) {
                break;
            }

            // Exponential backoff
            const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
            console.log(`[WialonService] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }

    throw lastError;
};

/**
 * Parse Wialon API response and handle errors
 * Only numeric error codes are treated as errors
 * @param {any} data - Response data
 * @returns {any} - Parsed data
 * @throws {WialonError} - If response contains error
 */
const parseResponse = (data) => {
    // Only treat numeric error codes as errors (Wialon uses numbers for error codes)
    if (typeof data?.error === 'number') {
        const isAuthError = [1, 2, 4, 7].includes(data.error); // Common Wialon auth error codes
        throw new WialonError(
            `Wialon API Error: ${data.reason || getErrorReason(data.error)}`,
            data.error,
            isAuthError
        );
    }
    return data;
};

/**
 * Parse avl_evts response - this endpoint has a different format
 * It returns {tm: timestamp, events: [...]} on success
 * @param {any} data - Response data
 * @returns {any} - Parsed data
 * @throws {WialonError} - If response indicates error
 */
const parseEventsResponse = (data) => {
    // Check for numeric error code
    console.log("data=====>", data);
    if (typeof data?.error === 'number' || data?.error) {
        console.log("data=====>session active", false);
        const isAuthError = [1, 2, 4, 7].includes(data.error);
        throw new WialonError(
            `Wialon API Error: ${data.reason || getErrorReason(data.error)}`,
            data.error,
            isAuthError
        );
    }
    // avl_evts returns {tm: ..., events: [...]} on success
    // tm is the timestamp, events is an array of events
    return data;
};

/**
 * Get human-readable error reason from Wialon error code
 */
const getErrorReason = (errorCode) => {
    const errorCodes = {
        1: 'Invalid session',
        2: 'Invalid service name',
        3: 'Invalid result',
        4: 'Invalid input',
        5: 'Error performing request',
        6: 'Unknown error',
        7: 'Access denied',
        8: 'Invalid user name or password',
        9: 'Authorization server unavailable',
        10: 'Reached limit of session',
    };
    return errorCodes[errorCode] || 'Unknown error';
};

/**
 * Wialon API Service object
 */
const WialonService = {
    /**
     * Duplicate session - Creates a new session from existing one
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} sid - Original session ID
     * @returns {Promise<{eid: string, user: object}>} - New session data
     */
    duplicateSession: async (baseUrl, sid) => {
        const params = encodeURIComponent(JSON.stringify({
            operateAs: "",
            continueCurrentSession: true
        }));
        const url = `${baseUrl}/wialon/ajax.html?svc=core/duplicate&params=${params}&sid=${sid}`;

        console.log('[WialonService] duplicateSession URL:', url);

        return withRetry(async () => {
            const data = await jsonpRequest(url);
            console.log('[WialonService] duplicateSession response:', data);
            return parseResponse(data);
        });
    },

    /**
     * Search for a single item (e.g., billing account)
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} sid - Session ID
     * @param {number} itemId - Item ID to search
     * @param {number} flags - Search flags (default: 1)
     * @returns {Promise<{item: object}>} - Item data
     */
    searchItem: async (baseUrl, sid, itemId, flags = 1) => {
        const params = encodeURIComponent(JSON.stringify({
            id: itemId,
            flags: flags
        }));
        const url = `${baseUrl}/wialon/ajax.html?svc=core/search_item&params=${params}&sid=${sid}`;

        return withRetry(async () => {
            const data = await jsonpRequest(url);
            return parseResponse(data);
        });
    },

    /**
     * Search for multiple items (e.g., units/vehicles)
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} sid - Session ID
     * @param {object} searchSpec - Search specification
     * @returns {Promise<{items: array}>} - Items data
     */
    searchItems: async (baseUrl, sid, searchSpec = {}) => {
        const defaultSpec = {
            itemsType: "avl_unit",
            propName: "sys_name",
            propValueMask: "*",
            sortType: "sys_name",
            propType: "property"
        };

        const spec = { ...defaultSpec, ...searchSpec };
        const params = encodeURIComponent(JSON.stringify({
            spec,
            force: 0,
            flags: 1,
            from: 0,
            to: 0
        }));

        const url = `${baseUrl}/wialon/ajax.html?svc=core/search_items&params=${params}&sid=${sid}`;

        return withRetry(async () => {
            const data = await jsonpRequest(url);
            return parseResponse(data);
        });
    },

    /**
     * Keep-alive ping - Keeps session active
     * The avl_evts endpoint returns {tm: timestamp, events: [...]}
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} sid - Session ID
     * @returns {Promise<{tm: number, events: array}>} - Response with timestamp and events
     */
    keepAlive: async (baseUrl, sid) => {
        const url = `${baseUrl}/avl_evts?sid=${sid}`;

        // Use single attempt for keep-alive (caller handles retry logic)
        const data = await jsonpRequest(url, 10000); // 10s timeout for keep-alive
        return parseEventsResponse(data);
    },

    /**
     * Validate session - Check if session is still valid
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} sid - Session ID
     * @returns {Promise<boolean>} - True if valid
     */
    validateSession: async (baseUrl, sid) => {
        try {
            const data = await WialonService.keepAlive(baseUrl, sid);
            return data?.tm !== undefined;
        } catch (error) {
            console.log('[WialonService] Session validation failed:', error.message);
            return false;
        }
    },

    /**
     * Re-authenticate using auth hash/token
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} authHash - Authentication hash/token
     * @returns {Promise<{eid: string, user: object}>} - New session data
     */
    loginWithHash: async (baseUrl, authHash) => {
        const params = encodeURIComponent(JSON.stringify({
            token: authHash
        }));
        const url = `${baseUrl}/wialon/ajax.html?svc=token/login&params=${params}`;

        return withRetry(async () => {
            const data = await jsonpRequest(url);
            return parseResponse(data);
        });
    },

    /**
     * Load all resources with geofence zone data
     * Equivalent to the extractor's updateDataFlags call with avl_resource + zones flag
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} sid - Session ID
     * @returns {Promise<{items: array}>} - Resource items with zone data
     */
    loadResources: async (baseUrl, sid) => {
        const spec = {
            itemsType: "avl_resource",
            propName: "sys_name",
            propValueMask: "*",
            sortType: "sys_name",
            propType: "property"
        };
        // flags: 0x1 (base) | 0x1000 (zones) = 0x1001 = 4097
        const params = encodeURIComponent(JSON.stringify({
            spec,
            force: 1,
            flags: 4097,
            from: 0,
            to: 0
        }));
        const url = `${baseUrl}/wialon/ajax.html?svc=core/search_items&params=${params}&sid=${sid}`;

        return withRetry(async () => {
            const data = await jsonpRequest(url);
            return parseResponse(data);
        });
    },

    /**
     * Logout - End session
     * @param {string} baseUrl - Wialon API base URL
     * @param {string} sid - Session ID
     * @returns {Promise<void>}
     */
    logout: async (baseUrl, sid) => {
        const url = `${baseUrl}/wialon/ajax.html?svc=core/logout&params={}&sid=${sid}`;

        try {
            await jsonpRequest(url, 5000);
        } catch (error) {
            // Ignore logout errors
            console.log('[WialonService] Logout error (ignored):', error.message);
        }
    },
};

export { WialonError };
export default WialonService;
